import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { TextLines } from "@/components/tool-kit/diagrams/svg-parts";
import {
  type DrawnEdge,
  type DrawnLabel,
  LayeredArrows,
  LayeredLabels,
} from "@/components/tools/diagrams-more/layered-edges";
import { LINE_HEIGHT, type Rect } from "@/lib/tool-kit/charts/layout";
import { tint } from "@/lib/tool-kit/diagrams/color";
import {
  DATA_SKEW,
  FLOW_FONT,
  type FlowLayout,
  type FlowShape,
  LABEL_FONT,
  type PlacedStep,
} from "@/lib/tool-kit/diagrams-more/flowchart";
import { titledFrame } from "@/lib/tool-kit/diagrams-more/frame";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A flowchart from `layoutFlowchart`. Each step is drawn as its ISO 5807 symbol — a
 * terminator, process, decision, data, predefined process, preparation, stored data,
 * document or connector — in black outline on white, or tinted by symbol type. Shape
 * carries the meaning either way, so the chart reads in greyscale and for readers who
 * cannot tell colours apart.
 */

const HEX = 16;
const CAP = 8;
const BAR = 10;
const WAVE = 9;

const FILLS: Record<FlowShape, string> = {
  terminal: CHART_COLORS[2],
  process: CHART_COLORS[0],
  decision: CHART_COLORS[1],
  data: CHART_COLORS[4],
  predefined: CHART_COLORS[5],
  preparation: CHART_COLORS[3],
  database: CHART_INK.axis,
  document: CHART_COLORS[4],
  connector: CHART_INK.axis,
};

function points(list: readonly [number, number][]): string {
  return list.map(([x, y]) => `${px(x)},${px(y)}`).join(" ");
}

interface Paint {
  fill: string;
  stroke: string;
  strokeWidth: number;
}

function Outline({
  shape,
  rect,
  paint,
}: {
  shape: FlowShape;
  rect: Rect;
  paint: Paint;
}) {
  const { x, y, width: w, height: h } = rect;
  const cx = x + w / 2;
  const cy = y + h / 2;
  switch (shape) {
    case "terminal":
      return (
        <rect
          height={px(h)}
          rx={px(h / 2)}
          width={px(w)}
          x={px(x)}
          y={px(y)}
          {...paint}
        />
      );
    case "decision":
      return (
        <polygon
          points={points([
            [cx, y],
            [x + w, cy],
            [cx, y + h],
            [x, cy],
          ])}
          {...paint}
        />
      );
    case "data":
      return (
        <polygon
          points={points([
            [x + DATA_SKEW, y],
            [x + w, y],
            [x + w - DATA_SKEW, y + h],
            [x, y + h],
          ])}
          {...paint}
        />
      );
    case "preparation":
      return (
        <polygon
          points={points([
            [x + HEX, y],
            [x + w - HEX, y],
            [x + w, cy],
            [x + w - HEX, y + h],
            [x + HEX, y + h],
            [x, cy],
          ])}
          {...paint}
        />
      );
    case "database":
      return (
        <g>
          <path
            d={`M${px(x)} ${px(y + CAP)}A${px(w / 2)} ${CAP} 0 0 1 ${px(x + w)} ${px(y + CAP)}V${px(y + h - CAP)}A${px(w / 2)} ${CAP} 0 0 1 ${px(x)} ${px(y + h - CAP)}Z`}
            {...paint}
          />
          <path
            d={`M${px(x)} ${px(y + CAP)}A${px(w / 2)} ${CAP} 0 0 0 ${px(x + w)} ${px(y + CAP)}`}
            fill="none"
            stroke={paint.stroke}
            strokeWidth={paint.strokeWidth}
          />
        </g>
      );
    case "document":
      return (
        <path
          d={`M${px(x)} ${px(y)}H${px(x + w)}V${px(y + h - WAVE)}Q${px(x + 0.75 * w)} ${px(y + h - 2 * WAVE)} ${px(cx)} ${px(y + h - WAVE)}T${px(x)} ${px(y + h - WAVE)}Z`}
          {...paint}
        />
      );
    case "connector":
      return <circle cx={px(cx)} cy={px(cy)} r={px(w / 2)} {...paint} />;
    default:
      return (
        <rect
          height={px(h)}
          rx={2}
          width={px(w)}
          x={px(x)}
          y={px(y)}
          {...paint}
        />
      );
  }
}

function StepSymbol({ step, colored }: { step: PlacedStep; colored: boolean }) {
  const { rect, symbol, node } = step;
  const paint: Paint = {
    fill: colored ? tint(FILLS[node.shape], 0.82) : CHART_INK.background,
    stroke: colored ? FILLS[node.shape] : CHART_INK.text,
    strokeWidth: colored ? 1.75 : 1.5,
  };
  const lineHeight = FLOW_FONT * LINE_HEIGHT;
  const textHeight = symbol.lines.length * lineHeight;
  return (
    <g>
      <Outline paint={paint} rect={rect} shape={node.shape} />
      {node.shape === "predefined" ? (
        <g>
          {[rect.x + BAR, rect.x + rect.width - BAR].map((x) => (
            <line
              key={x}
              stroke={paint.stroke}
              strokeWidth={paint.strokeWidth}
              x1={px(x)}
              x2={px(x)}
              y1={px(rect.y)}
              y2={px(rect.y + rect.height)}
            />
          ))}
        </g>
      ) : null}
      <TextLines
        fill={CHART_INK.text}
        fontSize={FLOW_FONT}
        lineHeight={lineHeight}
        lines={symbol.lines}
        top={rect.y + rect.height / 2 + symbol.textShift - textHeight / 2}
        x={rect.x + rect.width / 2}
      />
    </g>
  );
}

export function FlowchartDiagram({
  id,
  layout,
  title,
  label,
  description,
  colored,
}: {
  id: string;
  layout: FlowLayout;
  /** The title drawn inside the image; empty for an APA figure. */
  title: string;
  label: string;
  description: string;
  colored: boolean;
}) {
  const frame = titledFrame(layout.width, layout.height, title);
  const edges: DrawnEdge[] = layout.links.map((link) => ({
    key: `edge-${link.path.index}`,
    points: link.path.points,
  }));
  const labels: DrawnLabel[] = layout.links.flatMap((link) =>
    link.path.label
      ? [
          {
            key: `label-${link.path.index}`,
            rect: link.path.label,
            lines: [link.labelText],
          },
        ]
      : []
  );
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitle title={frame.title} />
      <g transform={`translate(${px(frame.dx)} ${px(frame.dy)})`}>
        <LayeredArrows edges={edges} />
        {layout.steps.map((step) => (
          <StepSymbol colored={colored} key={step.node.id} step={step} />
        ))}
        <LayeredLabels fontSize={LABEL_FONT} labels={labels} />
      </g>
    </ChartSvg>
  );
}
